/**
 * MaintenanceSchedule — displays scheduled and in-progress maintenance windows.
 */

import type { MaintenanceWindow, MaintenanceWindowStatus } from '../../api/status.js';

// ── Config ────────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<MaintenanceWindowStatus, { bg: string; text: string; label: string }> = {
  scheduled:   { bg: 'bg-blue-50',   text: 'text-blue-700',   label: 'Scheduled' },
  in_progress: { bg: 'bg-amber-50',  text: 'text-amber-700',  label: 'In Progress' },
  completed:   { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Completed' },
  cancelled:   { bg: 'bg-neutral-50', text: 'text-neutral-600', label: 'Cancelled' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameDay = s.toDateString() === e.toDateString();

  if (sameDay) {
    return `${s.toLocaleDateString(undefined, { dateStyle: 'medium' })} · ${s.toLocaleTimeString(undefined, { timeStyle: 'short' })} – ${e.toLocaleTimeString(undefined, { timeStyle: 'short' })}`;
  }

  return `${s.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} – ${e.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`;
}

// ── MaintenanceCard ───────────────────────────────────────────────────────────

interface MaintenanceCardProps {
  window: MaintenanceWindow;
}

function MaintenanceCard({ window: mw }: MaintenanceCardProps) {
  const config = STATUS_CONFIG[mw.status];

  return (
    <div className="border border-neutral-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-neutral-900 text-sm">{mw.title}</h4>
          {mw.description && (
            <p className="text-sm text-neutral-600 mt-0.5">{mw.description}</p>
          )}
        </div>
        <span
          className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.text} ${config.bg}`}
        >
          {config.label}
        </span>
      </div>

      <div className="text-xs text-neutral-500 space-y-1">
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{formatDateRange(mw.scheduled_start, mw.scheduled_end)}</span>
        </div>

        {mw.affected_components.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className="text-neutral-400">Affects:</span>
            <div className="flex flex-wrap gap-1">
              {mw.affected_components.map((id) => (
                <span key={id} className="bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded text-xs">
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MaintenanceSchedule ───────────────────────────────────────────────────────

interface MaintenanceScheduleProps {
  windows: MaintenanceWindow[];
  className?: string;
}

export function MaintenanceSchedule({ windows, className = '' }: MaintenanceScheduleProps) {
  const active = windows.filter(
    (w) => w.status === 'scheduled' || w.status === 'in_progress',
  );

  if (active.length === 0) return null;

  return (
    <section className={className} aria-labelledby="maintenance-heading">
      <h2 id="maintenance-heading" className="text-lg font-semibold text-neutral-800 mb-3">
        Scheduled Maintenance
      </h2>
      <div className="space-y-3">
        {active.map((w) => (
          <MaintenanceCard key={w.id} window={w} />
        ))}
      </div>
    </section>
  );
}
